import React, { useEffect, useState } from 'react';
import Shell from '../Shell';
import { api } from '../api';
import { useToast } from '../ToastContext';

const blankForm = { name: '', defaultCurrency: 'USD', approvedManufacturerList: '' };

export default function Customers() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      setItems((await api.listCustomers()).items);
    } catch (e) {
      setError(e.message || 'Failed to load customers');
      toast.push(e.message || 'Failed to load customers', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function openEdit(c) {
    setForm({
      name: c.name || '',
      defaultCurrency: c.defaultCurrency || 'USD',
      approvedManufacturerList: (c.approvedManufacturerList || []).join(', '),
    });
    setEditingId(c._id);
    setShowNew(true);
  }

  function closeModal() {
    setShowNew(false);
    setEditingId(null);
    setForm(blankForm);
  }

  async function save() {
    try {
      const body = {
        ...form,
        approvedManufacturerList: form.approvedManufacturerList.split(/[,;]/).map((s) => s.trim()).filter(Boolean),
      };
      if (editingId) {
        await api.updateCustomer(editingId, body);
        toast.push('Customer updated');
      } else {
        await api.createCustomer(body);
        toast.push('Customer created');
      }
      closeModal();
      reload();
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    }
  }

  return (
    <Shell title="Customers" actions={<button className="btn" onClick={() => setShowNew(true)}>+ Add customer</button>}>
      <table className="table">
        <thead><tr><th>Name</th><th>Currency</th><th>Approved manufacturers</th><th></th></tr></thead>
        <tbody>
          {loading && (
            <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: 'var(--ink-500)' }}>Loading customers…</td></tr>
          )}
          {!loading && error && (
            <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24 }}>
              <div className="banner risk" style={{ display: 'inline-flex', textAlign: 'left' }}>
                <div style={{ flex: 1 }}>Couldn’t load customers. {error}</div>
                <button className="btn btn-sm btn-secondary" onClick={reload}>Retry</button>
              </div>
            </td></tr>
          )}
          {!loading && !error && items.length === 0 && (
            <tr>
              <td colSpan={4} style={{ textAlign: 'center', padding: 32, color: 'var(--ink-500)' }}>
                No customers yet. Add your first customer.
              </td>
            </tr>
          )}
          {items.map((c) => (
            <tr key={c._id}>
              <td><b>{c.name}</b></td>
              <td>{c.defaultCurrency}</td>
              <td>{(c.approvedManufacturerList || []).join(', ') || '—'}</td>
              <td><button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)}>Edit</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      {showNew && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,30,0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 480 }}>
            <h2 className="card-title">{editingId ? 'Edit customer' : 'Add customer'}</h2>
            <label className="field"><span className="label">Name</span>
              <input className="input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </label>
            <label className="field"><span className="label">Default currency</span>
              <select className="select" value={form.defaultCurrency} onChange={(e) => setForm((f) => ({ ...f, defaultCurrency: e.target.value }))}>
                {['USD', 'EUR', 'GBP', 'INR', 'AED', 'SGD'].map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="field"><span className="label">Approved manufacturers (comma)</span>
              <input className="input" value={form.approvedManufacturerList} onChange={(e) => setForm((f) => ({ ...f, approvedManufacturerList: e.target.value }))} />
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
              <button className="btn" onClick={save} disabled={!form.name}>{editingId ? 'Save changes' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
